/*
 * Copyright (c) 2023 - 2026. Bluemate B.V.
 */
import './style/bluemateButton.scss';

import React, {CSSProperties, MouseEventHandler, ReactNode, useMemo} from 'react';

import {ElementPosition, OrnamentShape, preventDefault, voidFunction} from '@dvrd/dvr-controls';

import {Icon} from '@iconify/react';

import classNames from 'classnames';

import {
    BluemateControlAlign,
    BluemateControlColor,
    BluemateControlShape,
    BluemateControlSize
} from '../../../util/types';

interface Props {
    onClick?: MouseEventHandler;
    href?: string;
    label: ReactNode;
    ornaments?: OrnamentShape | OrnamentShape[];
    /** Work started by this button is running: shows a spinner next to the label
     *  and swallows further clicks until it finishes. */
    loading?: boolean;
    disabled?: boolean;
    className?: string;
    labelClassName?: string;
    ornamentClassName?: string;
    secondary?: boolean;
    tertiary?: boolean;
    /** Tinted fill + solid coloured border + rounded-rectangle corners. Colour
     * follows the `color` prop (defaults to blue). Shared design-system option
     * used by the notification config controls and PortalSelect. */
    outlined?: boolean;
    /** Square button holding a single icon as its label; sized to the matching
     * PortalSelect trigger height so it lines up with selects in the same row. */
    iconOnly?: boolean;
    color?: BluemateControlColor;
    size?: BluemateControlSize;
    shape?: BluemateControlShape;
    align?: BluemateControlAlign;
    id?: string;
    /**
     * Tooltip required props, without these the tooltips don't show
     */
    onMouseEnter?: MouseEventHandler;
    onMouseLeave?: MouseEventHandler;
    ref?: React.RefObject<any>;
}

export default function BluemateButton(props: Props) {
    const {
        ornaments,
        ornamentClassName,
        href,
        color = 'blue',
        disabled,
        loading,
        onClick,
        id,
        onMouseEnter,
        size = 'normal',
        shape = 'square',
        align = 'left',
        onMouseLeave,
        secondary,
        tertiary,
        outlined,
        iconOnly,
        className,
        ref,
        labelClassName,
        label
    } = props;
    const containerClassName = useMemo(
        () =>
            classNames(
                'bluemate-button',
                `color-${color}`,
                `size-${size}`,
                `shape-${shape}`,
                `align-${align}`,
                (disabled || loading) && 'disabled',
                loading && 'loading',
                secondary && 'secondary',
                tertiary && 'tertiary',
                outlined && 'outlined',
                iconOnly && 'icon-only',
                className
            ),
        [color, size, shape, align, secondary, tertiary, outlined, iconOnly, className, disabled, loading]
    );

    function getContainerStyle(): CSSProperties {
        const style: CSSProperties = {gridTemplateColumns: '1fr'};
        if (!ornaments || (Array.isArray(ornaments) && !ornaments.length)) style.gridTemplateColumns = '1fr';
        let hasLeft: boolean = false,
            hasRight: boolean = false;
        if (Array.isArray(ornaments))
            for (const ornament of ornaments) {
                if (!ornament.placement || ornament.placement === ElementPosition.LEFT) hasLeft = true;
                else if (ornament.placement === ElementPosition.RIGHT) hasRight = true;
                if (hasLeft && hasRight) break;
            }
        else if (ornaments) {
            hasLeft = !ornaments.placement || ornaments.placement === ElementPosition.LEFT;
            hasRight = ornaments.placement === ElementPosition.RIGHT;
        }
        let columns: string = '1fr';
        if (hasRight) columns = `${columns} fit-content(100%)`;
        if (hasLeft) columns = `fit-content(100%) ${columns}`;
        style.gridTemplateColumns = columns;
        return style;
    }

    function renderLeftOrnament() {
        if (!ornaments || (Array.isArray(ornaments) && !ornaments.length)) return null;
        if (Array.isArray(ornaments)) {
            for (const ornament of ornaments) {
                if (!ornament.placement || ornament.placement === ElementPosition.LEFT) return renderOrnament(ornament);
            }
        } else if (!ornaments.placement || ornaments.placement === ElementPosition.LEFT)
            return renderOrnament(ornaments);
        return null;
    }

    function renderRightOrnament() {
        if (!ornaments || (Array.isArray(ornaments) && !ornaments.length)) return null;
        if (Array.isArray(ornaments)) {
            for (const ornament of ornaments) {
                if (ornament.placement === ElementPosition.RIGHT) return renderOrnament(ornament);
            }
        } else if (ornaments.placement === ElementPosition.RIGHT) return renderOrnament(ornaments);
        return null;
    }

    function renderOrnament(ornament: OrnamentShape) {
        if (typeof ornament.element === 'string')
            return <span className={classNames('bluemate-input-ornament', ornamentClassName)}>{ornament.element}</span>;
        else
            return React.cloneElement(ornament.element, {
                className: classNames(ornament.element.props.className, ornamentClassName)
            });
    }

    function renderContent() {
        const LabelTag = href ? 'span' : 'label';
        return (
            <>
                {renderLeftOrnament()}
                <LabelTag className={classNames('bluemate-button-label', labelClassName)}>
                    {label}
                    {!!loading && <Icon icon='mdi:loading' className='bluemate-button-spinner' />}
                </LabelTag>
                {renderRightOrnament()}
            </>
        );
    }

    if (href)
        return (
            <a
                className={containerClassName}
                style={getContainerStyle()}
                href={href}
                target='_blank'
                rel='noopener noreferrer'
                onClick={disabled || loading ? preventDefault : onClick}
                id={id}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                ref={ref}
            >
                {renderContent()}
            </a>
        );

    return (
        <div
            className={containerClassName}
            style={getContainerStyle()}
            onClick={disabled || loading ? voidFunction : onClick}
            id={id}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            ref={ref}
        >
            {renderContent()}
        </div>
    );
}
